import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrashIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

const DeleteAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/delete-account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ confirm }),
      });
      if (res.ok) {
        setSuccess(true);
        localStorage.removeItem('token');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to delete account');
      }
    } catch (err) {
      setError('Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/dashboard/settings')}
        className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
      >
        ← Back to Settings
      </button>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Delete Account</h1>
      <p className="mb-6 text-sm text-gray-600">
        Permanently delete your account and all associated data. This action cannot be undone.
      </p>
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-600 mr-2" />
          <span className="text-red-900 font-medium">Warning: This is permanent!</span>
        </div>
        <ul className="list-disc pl-6 text-sm text-red-700 space-y-1 mb-4">
          <li>All your data will be deleted and cannot be recovered</li>
          <li>Active subscriptions will be cancelled</li>
          <li>You will lose access to your account immediately</li>
          <li>Your username will be released for others to use</li>
        </ul>
        <form onSubmit={handleDelete} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-red-900 mb-2">
              Type <span className="font-mono bg-red-100 px-2 py-0.5 rounded">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="block w-full rounded-md border-red-300 shadow-sm focus:border-red-500 focus:ring-red-500 text-sm"
              required
            />
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-100 p-3 rounded-md">{error}</div>
          )}
          {success && (
            <div className="flex items-center p-3 bg-green-50 rounded-md">
              <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-sm text-green-800">Account deleted. Redirecting...</span>
            </div>
          )}
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => navigate('/dashboard/settings')}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || confirm !== 'DELETE'}
              className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Deleting...' : 'Delete My Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
